import React, { useEffect } from "react";
import { Avatar, Button, Col, Form, Input, InputNumber, Modal, Row, Select, Spin } from "antd";
import {
    FolderOpenOutlined,
    FileTextOutlined,
    TeamOutlined,
    CalendarOutlined,
    ApartmentOutlined,
    SaveOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { AffaireService } from "../../services/AffaireService";
import { catchError } from "../../services/DaoService";
import Toast from "../../helpers/Toast";
import PropTypes from "prop-types";
import CustomFormItem from "../utils/form/CustomFormItem";
import { AiNameTrigger, AiNamePanel } from "../ai/AiNameSuggest";
import { useAiNameAssist } from "../../hooks/useAiNameAssist";
import UserService from "../../services/UserService";
import { PrioriteService } from "../../services/PrioriteService";
import { ModeleService } from "../../services/ModeleService";
import { DepartementService } from "../../services/DepartementService";
import { affairesState } from "../../state/actions/affairesState";
import style from "./AffaireEditForm.module.scss";

const AffaireEditForm = ({ visible, uid, onUpdate, onCancel }) => {
    const [form] = Form.useForm();
    const nomValue = Form.useWatch('nom', form);
    const nameAi = useAiNameAssist('affaire');
    const [editing, setEditing] = React.useState(false);
    const [validationErrors, setValidationErrors] = React.useState({});
    const [personnels, setPersonnels] = React.useState([]);
    const [priorites, setPriorites] = React.useState([]);
    const [affaire, setAffaire] = React.useState(null);
    const [modeles, setModeles] = React.useState([]);
    const [departements, setDepartements] = React.useState([]);

    useEffect(() => {
        Promise.all([
            UserService.getInstance().all(),
            PrioriteService.getInstance().all(),
            ModeleService.getInstance().all(),
            DepartementService.getInstance().all(),
        ])
            .then(([personnelsResponse, prioritesResponse, modelesResponse, departementsResponse]) => {
                setPersonnels(personnelsResponse.data);
                setPriorites(prioritesResponse.data);
                setModeles(modelesResponse.data);
                setDepartements(departementsResponse.data);
            })
            .catch(catchError);
    }, []);

    useEffect(() => {
        if (uid) {
            setEditing(true);
            AffaireService.getInstance()
                .find(uid)
                .then((response) => {
                    form.setFieldsValue(response.data);
                    setAffaire(response.data);
                })
                .catch(catchError)
                .finally(() => setEditing(false));
        }
    }, [uid, form]);

    const handleSave = () => {
        setEditing(true);
        form
            .validateFields()
            .then((values) => {
                AffaireService.getInstance()
                    .update(uid, values)
                    .then((response) => {
                        Toast.success(response.message);
                        affairesState.updateAffaireInList(response.data);
                        onUpdate(response.data);
                    })
                    .catch((error) => {
                        const errors = error.response?.data;
                        if (errors?.validation && Object.keys(errors.errors).length) {
                            setValidationErrors(errors.errors);
                        } else {
                            catchError(error);
                        }
                    })
                    .finally(() => setEditing(false));
            })
            .catch(() => setEditing(false));
    };

    const TitleNode = (
        <div className={style.modalTitle}>
            <span className={style.titleIcon}>
                <FolderOpenOutlined />
            </span>
            <div>
                <span className={style.titleText}>Modifier le dossier</span>
                <span className={style.titleSubtitle}>
                    Mettez à jour les informations de ce dossier.
                </span>
            </div>
        </div>
    );

    const FooterNode = (
        <div className={style.modalFooter}>
            <Button className={style.cancelBtn} onClick={onCancel}>
                Annuler
            </Button>
            <Button
                type="primary"
                className={style.submitBtn}
                icon={<SaveOutlined />}
                loading={editing}
                onClick={handleSave}
            >
                Enregistrer les modifications
            </Button>
        </div>
    );

    return (
        <Modal
            width={780}
            centered
            className={style.affaireEditModal}
            wrapClassName={style.affaireEditModalWrap}
            destroyOnHidden
            open={visible}
            onCancel={onCancel}
            title={TitleNode}
            footer={FooterNode}
        >
            <Spin spinning={editing}>
                <Form
                    layout="vertical"
                    form={form}
                    name="editAffaireForm"
                    className={style.form}
                    initialValues={{ enabled: 1 }}
                >
                    {/* Section 1: Identification */}
                    <div className={style.sectionHeader}>
                        <span className={style.sectionIcon}>
                            <FileTextOutlined />
                        </span>
                        <div>
                            <p className={style.sectionTitle}>Identification</p>
                            <p className={style.sectionHint}>Nom et description du dossier</p>
                        </div>
                    </div>

                    <Row gutter={16} className={style.formGrid}>
                        <Col xs={24}>
                            <CustomFormItem
                                label="Nom"
                                name="nom"
                                rules={[{ required: true, message: "Veuillez saisir le nom!" }]}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Input
                                    className={style.control}
                                    placeholder="Nom du dossier"
                                    suffix={
                                        <AiNameTrigger
                                            loading={nameAi.loading}
                                            disabled={!nomValue || nomValue.trim().length < 3}
                                            onClick={() => nameAi.run(form.getFieldValue('nom'))}
                                        />
                                    }
                                />
                            </CustomFormItem>
                            <AiNamePanel
                                status={nameAi.status}
                                proposal={nameAi.proposal}
                                onApply={(text) => { form.setFieldsValue({ nom: text }); nameAi.reset(); }}
                                onReset={nameAi.reset}
                            />
                        </Col>
                        <Col xs={24}>
                            <CustomFormItem
                                label="Description"
                                name="description"
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Input.TextArea
                                    rows={5}
                                    className={style.textarea}
                                    placeholder="Décrivez l'objet ou le contexte de ce dossier…"
                                />
                            </CustomFormItem>
                        </Col>
                    </Row>

                    {/* Section 2: Responsabilité */}
                    <div className={style.sectionHeader}>
                        <span className={style.sectionIcon}>
                            <TeamOutlined />
                        </span>
                        <div>
                            <p className={style.sectionTitle}>Responsabilité</p>
                            <p className={style.sectionHint}>Responsable et département</p>
                        </div>
                    </div>

                    <Row gutter={16} className={style.formGrid}>
                        <Col xs={24} sm={12}>
                            <CustomFormItem
                                label="Responsable"
                                name="responsable_id"
                                rules={[{ required: true, message: "Sélectionner le responsable" }]}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Select
                                    className={style.control}
                                    showSearch
                                    optionFilterProp="full_name"
                                    placeholder="Sélectionner le responsable"
                                    options={personnels.map((personnel) => ({
                                        label: (
                                            <span className={style.userOption}>
                                                <Avatar
                                                    src={personnel.avatar}
                                                    alt={personnel.name}
                                                    size={22}
                                                    icon={<UserOutlined />}
                                                />
                                                <span>{personnel.name}</span>
                                            </span>
                                        ),
                                        value: personnel.id,
                                        full_name: personnel.name,
                                    }))}
                                />
                            </CustomFormItem>
                        </Col>

                        <Col xs={24} sm={12}>
                            <CustomFormItem
                                label="Département"
                                name="departement_id"
                                extra="Ce champ n'est pas obligatoire"
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Select
                                    className={style.control}
                                    showSearch
                                    optionFilterProp="searchLabel"
                                    allowClear
                                    placeholder="Sélectionner le département"
                                    options={departements.map((d) => ({
                                        label: (
                                            <span className={style.optionInline}>
                                                <ApartmentOutlined />
                                                {d.nom} ({d.code})
                                            </span>
                                        ),
                                        value: d.id,
                                        searchLabel: `${d.nom} (${d.code})`,
                                    }))}
                                />
                            </CustomFormItem>
                        </Col>
                    </Row>

                    {/* Section 3: Planification */}
                    <div className={style.sectionHeader}>
                        <span className={style.sectionIcon}>
                            <CalendarOutlined />
                        </span>
                        <div>
                            <p className={style.sectionTitle}>Planification</p>
                            <p className={style.sectionHint}>Priorité, échéance, budget et modèle</p>
                        </div>
                    </div>

                    <Row gutter={16} className={style.formGrid}>
                        <Col xs={24} sm={12}>
                            <CustomFormItem
                                label="Priorité"
                                name="priorite_id"
                                rules={[{ required: true, message: "Veuillez sélectionner la priorité!" }]}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Select
                                    className={style.control}
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Sélectionner la priorité"
                                    options={priorites.map((p) => ({
                                        label: (
                                            <span className={style.optionInline}>
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        background: p.couleur,
                                                        display: "inline-block",
                                                    }}
                                                />
                                                {p.nom}
                                            </span>
                                        ),
                                        value: p.id,
                                    }))}
                                />
                            </CustomFormItem>
                        </Col>

                        <Col xs={24} sm={12}>
                            <CustomFormItem
                                label="Date limite"
                                name="date_limite"
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <Input type="date" className={style.control} />
                            </CustomFormItem>
                        </Col>

                        <Col xs={24} sm={12}>
                            <CustomFormItem
                                label="Budget"
                                name="budget"
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                            >
                                <InputNumber
                                    className={style.numberControl}
                                    min={0}
                                    suffix={affaire?.monnaie?.symbole}
                                />
                            </CustomFormItem>
                        </Col>

                        <Col xs={24}>
                            <CustomFormItem
                                label="Modèle d'affaire"
                                name="modele_id"
                                rules={[{ required: true, message: "Veuillez sélectionner le modèle d'affaire" }]}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                help={
                                    <span className={style.dangerHelp}>
                                        Attention&nbsp;! Changer le modèle d&apos;affaire peut affecter les données de l&apos;affaire. Si vous changez le modèle, l&apos;affaire sera mis à la première étape du nouveau modèle.
                                    </span>
                                }
                            >
                                <Select
                                    className={style.control}
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Sélectionner le modèle d'affaire"
                                    options={modeles.map((m) => ({ label: m.nom, value: m.id }))}
                                />
                            </CustomFormItem>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Modal>
    );
};

export default AffaireEditForm;

AffaireEditForm.propTypes = {
    visible: PropTypes.bool,
    onUpdate: PropTypes.func,
    onCancel: PropTypes.func,
    uid: PropTypes.string.isRequired,
};
